import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddOutlined,
  CheckCircleOutline,
  EditOutlined,
  Inventory2Outlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  SearchOutlined,
  ToggleOffOutlined,
  ToggleOnOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import EditProductDialog from "./EditProductDialog";
import ApiConfig from "../../Config";
import LoaderScreen from "../ProgressCircle/LoaderScreen";
import productService from "../../repository/productRepository";

const ViewProducts = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const isDark = theme.palette.mode === "dark";

  const accentColor = isDark ? "#D1B99A" : "#806B52";
  const accentHoverColor = isDark ? "#BFA786" : "#6D5B45";
  const tableHeaderBg = isDark ? "#211F1C" : "#F6F2ED";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [sortField, setSortField] = useState("productName");
  const [sortOrder, setSortOrder] = useState("asc");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const fetchProducts = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response = await productService.getAllProducts();
      const productsData = Array.isArray(response?.data)
        ? response.data.map((item) => ({
            id: item.product?.productId,
            productId: item.product?.productId,
            productName: item.product?.title || "Untitled Product",
            product_ShortDescription:
              item.product?.description || "No description",
            s_Price: item.product?.rentAmount ?? 0,
            size: item.product?.size || "N/A",
            productStatus: Boolean(item.productStatus),
            productImage: Array.isArray(item.productImage)
              ? item.productImage
              : [],
          }))
        : [];

      setProducts(productsData);
    } catch (err) {
      console.error("Failed to fetch products:", err);

      setError(err.response?.data?.message || "Failed to fetch products");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    const search = searchTerm.trim().toLowerCase();

    if (search) {
      filtered = filtered.filter((product) => {
        return (
          String(product.productName || "")
            .toLowerCase()
            .includes(search) ||
          String(product.productId || "")
            .toLowerCase()
            .includes(search) ||
          String(product.product_ShortDescription || "")
            .toLowerCase()
            .includes(search) ||
          String(product.size || "")
            .toLowerCase()
            .includes(search)
        );
      });
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((product) =>
        statusFilter === "active"
          ? product.productStatus
          : !product.productStatus,
      );
    }

    filtered.sort((a, b) => {
      let first = a[sortField];
      let second = b[sortField];

      if (typeof first === "string") {
        first = first.toLowerCase();
      }

      if (typeof second === "string") {
        second = second.toLowerCase();
      }

      if (first < second) {
        return sortOrder === "asc" ? -1 : 1;
      }

      if (first > second) {
        return sortOrder === "asc" ? 1 : -1;
      }

      return 0;
    });

    return filtered;
  }, [products, searchTerm, statusFilter, sortField, sortOrder]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  const activeProducts = products.filter(
    (product) => product.productStatus,
  ).length;

  const inactiveProducts = products.filter(
    (product) => !product.productStatus,
  ).length;

  const displayedProducts = filteredProducts.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / rowsPerPage),
  );

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setDialogOpen(true);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleSaveProduct = async () => {
    setDialogOpen(false);
    setSuccess("Product updated successfully");
    await fetchProducts();
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return null;
    }

    return sortOrder === "asc" ? (
      <KeyboardArrowUp sx={{ fontSize: 17 }} />
    ) : (
      <KeyboardArrowDown sx={{ fontSize: 17 }} />
    );
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === "") {
      return "₹0";
    }

    return `₹${Number(price).toLocaleString("en-IN")}`;
  };

  const getProductImage = (product) => {
    return product.productImage?.[0] || "";
  };

  const renderSortHeader = (label, field) => (
    <Box
      onClick={() => handleSort(field)}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        cursor: "pointer",
        userSelect: "none",
        color: "text.primary",
        "&:hover": {
          color: accentColor,
        },
      }}
    >
      <Typography component="span" variant="body2" fontWeight={700}>
        {label}
      </Typography>

      {getSortIcon(field)}
    </Box>
  );

  if (loading) {
    return <LoaderScreen />;
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: "background.default",
        overflow: "auto",
        px: {
          xs: 1.5,
          sm: 2,
          md: 3,
        },
        py: {
          xs: 2,
          md: 3,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            lg: "row",
          },
          alignItems: {
            xs: "stretch",
            lg: "center",
          },
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Inventory2Outlined
              sx={{
                fontSize: 30,
                color: accentColor,
              }}
            />

            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: "text.primary",
                letterSpacing: "-0.5px",
              }}
            >
              Products
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              color: "text.secondary",
            }}
          >
            Manage your Hindustan Wardrobe rental inventory
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              width: {
                xs: "100%",
                sm: 320,
              },
              height: 44,
              px: 1.5,
              borderRadius: 2,
              backgroundColor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
              transition: "all 0.2s ease",

              "&:focus-within": {
                borderColor: accentColor,
                boxShadow: isDark
                  ? "0 0 0 3px rgba(209,185,154,0.08)"
                  : "0 0 0 3px rgba(128,107,82,0.08)",
              },
            }}
          >
            <SearchOutlined
              sx={{
                mr: 1,
                color: "text.secondary",
              }}
            />

            <InputBase
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                flex: 1,
                color: "text.primary",
                fontSize: 14,
              }}
            />
          </Paper>

          <Button
            variant="contained"
            disabled
            startIcon={<AddOutlined />}
            onClick={() => navigate("/add-single-product")}
            sx={{
              height: 44,
              px: 2.2,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              whiteSpace: "nowrap",
              backgroundColor: accentColor,
              color: isDark ? "#211F1C" : "#FFFFFF",
              boxShadow: "none",

              "&:hover": {
                backgroundColor: accentHoverColor,
                boxShadow: "none",
              },
            }}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(3, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.2,
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: accentColor,
                backgroundColor: isDark
                  ? "rgba(209,185,154,0.12)"
                  : "rgba(128,107,82,0.10)",
              }}
            >
              <Inventory2Outlined />
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Products
              </Typography>

              <Typography variant="h5" fontWeight={700} color="text.primary">
                {products.length}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.2,
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#2e7d32",
                backgroundColor: "rgba(46,125,50,0.10)",
              }}
            >
              <CheckCircleOutline />
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Active Products
              </Typography>

              <Typography variant="h5" fontWeight={700} color="text.primary">
                {activeProducts}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.2,
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ed6c02",
                backgroundColor: "rgba(237,108,2,0.10)",
              }}
            >
              <ToggleOffOutlined />
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Inactive Products
              </Typography>

              <Typography variant="h5" fontWeight={700} color="text.primary">
                {inactiveProducts}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Filter
          </Typography>

          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            sx={{
              minWidth: 130,
              borderRadius: 2,

              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "divider",
              },

              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: accentColor,
              },

              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: accentColor,
              },
            }}
          >
            <MenuItem value="all">All Products</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Rows
          </Typography>

          <Select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            size="small"
            sx={{
              minWidth: 75,
              borderRadius: 2,

              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "divider",
              },

              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: accentColor,
              },

              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: accentColor,
              },
            }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>

          <Tooltip title="Refresh products">
            <span>
              <IconButton
                onClick={() => fetchProducts(true)}
                disabled={refreshing}
                sx={{
                  width: 38,
                  height: 38,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  color: accentColor,

                  "&:hover": {
                    backgroundColor: isDark
                      ? "rgba(209,185,154,0.08)"
                      : "rgba(128,107,82,0.08)",
                  },
                }}
              >
                {refreshing ? (
                  <CircularProgress
                    size={18}
                    sx={{
                      color: accentColor,
                    }}
                  />
                ) : (
                  <Inventory2Outlined
                    sx={{
                      fontSize: 19,
                    }}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        <Table
          stickyHeader
          sx={{
            minWidth: 1100,
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: tableHeaderBg,
              }}
            >
              <TableCell
                sx={{
                  backgroundColor: tableHeaderBg,
                  fontWeight: 700,
                }}
              >
                {renderSortHeader("Product ID", "productId")}
              </TableCell>

              <TableCell
                sx={{
                  backgroundColor: tableHeaderBg,
                  fontWeight: 700,
                }}
              >
                {renderSortHeader("Product", "productName")}
              </TableCell>

              <TableCell
                sx={{
                  backgroundColor: tableHeaderBg,
                  fontWeight: 700,
                }}
              >
                Image
              </TableCell>

              <TableCell
                sx={{
                  backgroundColor: tableHeaderBg,
                  fontWeight: 700,
                }}
              >
                Description
              </TableCell>

              <TableCell
                sx={{
                  backgroundColor: tableHeaderBg,
                  fontWeight: 700,
                }}
              >
                {renderSortHeader("Rent Price", "s_Price")}
              </TableCell>

              <TableCell
                sx={{
                  backgroundColor: tableHeaderBg,
                  fontWeight: 700,
                }}
              >
                Size
              </TableCell>

              <TableCell
                sx={{
                  backgroundColor: tableHeaderBg,
                  fontWeight: 700,
                }}
              >
                Status
              </TableCell>

              <TableCell
                align="center"
                sx={{
                  backgroundColor: tableHeaderBg,
                  fontWeight: 700,
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {displayedProducts.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  sx={{
                    py: 8,
                    textAlign: "center",
                  }}
                >
                  <Inventory2Outlined
                    sx={{
                      fontSize: 46,
                      color: "text.disabled",
                    }}
                  />

                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{
                      mt: 1,
                      color: "text.primary",
                    }}
                  >
                    No products found
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                    }}
                  >
                    Try changing your search or filter
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayedProducts.map((product) => (
                <TableRow
                  key={product.id}
                  hover
                  sx={{
                    "&:last-child td, &:last-child th": {
                      border: 0,
                    },
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color: accentColor,
                      }}
                    >
                      {product.productId}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="text.primary"
                      sx={{
                        maxWidth: 190,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {product.productName}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{
                        width: 58,
                        height: 68,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "action.hover",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {getProductImage(product) ? (
                        <Box
                          component="img"
                          src={getProductImage(product)}
                          alt={product.productName}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Inventory2Outlined
                          sx={{
                            color: "text.disabled",
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 250,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {product.product_ShortDescription}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="text.primary"
                    >
                      {formatPrice(product.s_Price)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={product.size || "N/A"}
                      size="small"
                      sx={{
                        height: 28,
                        borderRadius: 1.5,
                        fontWeight: 600,
                        color: accentColor,
                        backgroundColor: isDark
                          ? "rgba(209,185,154,0.10)"
                          : "rgba(128,107,82,0.08)",
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    {product.productStatus ? (
                      <Chip
                        icon={<CheckCircleOutline />}
                        label="Active"
                        size="small"
                        sx={{
                          height: 28,
                          borderRadius: 1.5,
                          fontWeight: 600,
                          color: "#2e7d32",
                          backgroundColor: "rgba(46,125,50,0.10)",
                          "& .MuiChip-icon": {
                            color: "#2e7d32",
                          },
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<ToggleOffOutlined />}
                        label="Inactive"
                        size="small"
                        sx={{
                          height: 28,
                          borderRadius: 1.5,
                          fontWeight: 600,
                          color: "#ed6c02",
                          backgroundColor: "rgba(237,108,2,0.10)",
                          "& .MuiChip-icon": {
                            color: "#ed6c02",
                          },
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="Edit product">
                      <IconButton
                        onClick={() => handleEdit(product)}
                        sx={{
                          width: 38,
                          height: 38,
                          color: accentColor,
                          borderRadius: 2,

                          "&:hover": {
                            backgroundColor: isDark
                              ? "rgba(209,185,154,0.08)"
                              : "rgba(128,107,82,0.08)",
                          },
                        }}
                      >
                        <EditOutlined />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing{" "}
          {filteredProducts.length === 0 ? 0 : (page - 1) * rowsPerPage + 1} -{" "}
          {Math.min(page * rowsPerPage, filteredProducts.length)} of{" "}
          {filteredProducts.length} products
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            disabled={page === 1}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            sx={{
              minWidth: 90,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              color: "text.primary",
              borderColor: "divider",

              "&:hover": {
                borderColor: accentColor,
              },
            }}
          >
            Previous
          </Button>

          <Box
            sx={{
              minWidth: 42,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              backgroundColor: isDark
                ? "rgba(209,185,154,0.10)"
                : "rgba(128,107,82,0.08)",
              color: accentColor,
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            {page}
          </Box>

          <Button
            variant="outlined"
            disabled={page >= totalPages}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            sx={{
              minWidth: 90,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              color: "text.primary",
              borderColor: "divider",

              "&:hover": {
                borderColor: accentColor,
              },
            }}
          >
            Next
          </Button>
        </Box>
      </Box>

      <EditProductDialog
        open={dialogOpen}
        onClose={async () => {
          setDialogOpen(false);
          await fetchProducts();
        }}
        product={selectedProduct}
        onSave={handleSaveProduct}
      />

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={5000}
        onClose={() => setSuccess("")}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() => setSuccess("")}
          severity="success"
          variant="filled"
          sx={{
            borderRadius: 2,
          }}
        >
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          variant="filled"
          sx={{
            borderRadius: 2,
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewProducts;
